import React, { useEffect, useState, useRef } from 'react';
import { getS3Client, getDynamoDBClient } from '../utils/awsClients';
import { parse } from 'papaparse'; 
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { Line } from 'react-chartjs-2'; 
import { Box, Button, CircularProgress, Dialog, DialogContent, DialogTitle, DialogActions, TextField } from '@mui/material';
import { ArrowBackIos, ArrowForwardIos, SaveAlt } from '@mui/icons-material'; 
import { Table, TableBody, TableRow, TableCell } from '@mui/material';
import { Snackbar, Alert, IconButton, Menu, MenuItem } from '@mui/material';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const s3Bucket = process.env.REACT_APP_USER_BUCKET;

const VideoDisplay = ({ selectedDevice, selectedExperiment }) => {
    const [data, setData] = useState([]);
    const [videos, setVideos] = useState([]);
    const [graphData, setGraphData] = useState(null);
    const [rawCSV, setRawCSV] = useState(''); // Store raw CSV data
    const [openModal, setOpenModal] = useState(false); 
    const [currentGraph, setCurrentGraph] = useState('Temperature');

    const [experimentParams, setExperimentParams] = useState(null);
    const [openParamsModal, setOpenParamsModal] = useState(false);
    const [openSnackbar, setOpenSnackbar] = useState(false);

    const [anchorEl, setAnchorEl] = useState(null); // For download menu
    const openMenu = Boolean(anchorEl);

    const [xAxisLabel, setXAxisLabel] = useState('Time (hours)');
    const [yAxisLabel, setYAxisLabel] = useState('Value');
    const [originalXAxisLabel, setOriginalXAxisLabel] = useState('Time (hours)');
    const [originalYAxisLabel, setOriginalYAxisLabel] = useState('Value');

    const [xMin, setXMin] = useState('');
    const [xMax, setXMax] = useState('');
    const [yMin, setYMin] = useState('');
    const [yMax, setYMax] = useState('');

    const chartRef = useRef(null);

    const fetchFilesDynamoDBData = async () => {
        try {
            if (selectedDevice) {
                const dynamoDB = await getDynamoDBClient();

                let params = {};
                if (selectedExperiment) {
                    params = {
                        TableName: "Experiment_folders",
                        FilterExpression: "#bucket = :bucketValue AND #experiment = :experimentValue",
                        ExpressionAttributeNames: {
                            "#bucket": "USER_BUCKET",
                            "#experiment": "experiment_name"
                        },
                        ExpressionAttributeValues: {
                            ":bucketValue": selectedDevice['device_bucket'],
                            ":experimentValue": selectedExperiment
                        }
                    };
                    const result = await dynamoDB.scan(params).promise();  
                    setData(result.Items);
                } else {
                    params = {
                        TableName: "Experiment_folders",
                        KeyConditionExpression: "#bucket = :bucketValue",
                        ExpressionAttributeNames: {
                            "#bucket": "USER_BUCKET"
                        },
                        ExpressionAttributeValues: {
                            ":bucketValue": selectedDevice['device_bucket'],
                        },
                        ScanIndexForward: false, 
                        Limit: 1  
                    };

                    const result = await dynamoDB.query(params).promise(); 
                    setData(result.Items);
                }
            }
        } catch (error) {
            console.error("Error fetching DynamoDB data:", error);
        }
    };

    const getPresignedUrlsForAllObjects = async (prefix) => {
        try {
            const s3 = await getS3Client();
            const listParams = {
                Bucket: process.env.REACT_APP_USER_BUCKET,
                Prefix: prefix
            };
    
            const data = await s3.listObjectsV2(listParams).promise();

            // Videos live at Videos/{experiment}/{plate}/{file}, where {file} is
            // either a single well (A1.webm) or the composited master. New masters
            // are VP8/WebM (browsers can't play the mp4v m*.mp4 we tried first);
            // legacy previews are .mp4, so accept both. Once a plate has a master,
            // it supersedes its per-well previews on the dashboard: the master is
            // the whole plate in one clip, so showing the wells too is just noise
            // (Advait, 2026-07-17). Plates with no master still show their wells.
            const isVideo = (k) => k.endsWith(".webm") || k.endsWith(".mp4");
            const baseName = (k) => k.split("/").pop().replace(/\.(webm|mp4)$/, "");
            const plateDirOf = (k) => k.split("/").slice(0, -1).join("/");
            const vids = (data.Contents || []).filter((it) => isVideo(it.Key));
            const masters = vids.filter((it) => baseName(it.Key) === "master");
            const platesWithMaster = new Set(masters.map((it) => plateDirOf(it.Key)));
            // A plate may briefly hold both master.webm (new) and master.mp4 (the
            // earlier mp4v attempt browsers can't play). Prefer the webm.
            const plateHasWebmMaster = new Set(
                masters.filter((it) => it.Key.endsWith(".webm")).map((it) => plateDirOf(it.Key))
            );
            const selected = vids.filter((it) => {
                const plateDir = plateDirOf(it.Key);
                if (platesWithMaster.has(plateDir)) {
                    if (baseName(it.Key) !== "master") return false;         // hide wells
                    if (plateHasWebmMaster.has(plateDir)) return it.Key.endsWith(".webm");
                    return true;
                }
                return true; // no master for this plate: keep the well previews
            });

            const videos = [];
            for (const item of selected) {
                const urlParams = {
                    Bucket: process.env.REACT_APP_USER_BUCKET,
                    Key: item.Key,
                    Expires: 3600
                };
                const url = await s3.getSignedUrlPromise('getObject', urlParams);
                const file = baseName(item.Key);
                const plate = item.Key.split("/")[3] || "";
                const type = item.Key.endsWith(".webm") ? "video/webm" : "video/mp4";
                // master -> label by plate; single well -> "{plate} {well}"
                const title = file === "master" ? plate : `${plate} ${file}`.trim();
                videos.push({ url, title, type, isMaster: file === "master" });
            }
            // masters first, then any remaining well previews
            videos.sort((a, b) => (b.isMaster === true) - (a.isMaster === true));
            setVideos(videos);
        } catch (error) {
            console.error("Error generating presigned URLs:", error);
        }
    };

    useEffect(() => {
        fetchFilesDynamoDBData();  
    }, [selectedDevice, selectedExperiment]);

    useEffect(() => {
        if (data.length > 0) { 
            const key = `${data[0]['USER_BUCKET']}/Videos/${data[0]['experiment_name']}`;
            getPresignedUrlsForAllObjects(key); 
        } else {
            setVideos([]);
        }
    }, [data]);

    const fetchCSVData = async () => {
        const s3 = await getS3Client();
        const s3Key = `${data[0]['USER_BUCKET']}/Experiments/${data[0]['experiment_name']}/temperature_data.csv`;
        try {
            const params = {
                Bucket: s3Bucket,
                Key: s3Key,
            };
    
            const response = await s3.getObject(params).promise();
            const csvData = response.Body.toString('utf-8');
    
            parse(csvData, {
                header: true,
                complete: (results) => {
                    setGraphData(results.data);
                },
            });
            setRawCSV(csvData); // Store the raw CSV data
            setOpenModal(true); 
        } catch (error) {
            console.error('Error fetching CSV from S3:', error);
        }
    };

    const fetchExperimentParams = async () => {
        const s3 = await getS3Client();
        const s3Key = `${data[0]['USER_BUCKET']}/Experiments/${data[0]['experiment_name']}/Incubator.json`;
        try {
            const params = {
                Bucket: s3Bucket,
                Key: s3Key,
            };
    
            const response = await s3.getObject(params).promise();
            const jsonData = JSON.parse(response.Body.toString('utf-8'));
            setExperimentParams(jsonData);
            setOpenParamsModal(true);
        } catch (error) {
            if (error.code === 'NoSuchKey') {
                setOpenSnackbar(true);
            } else {
                console.error('Error fetching JSON from S3:', error);
            }
        }
    };

    const getGraphData = () => {
        if (!graphData) return null;
      
        // 1. Calculate hours elapsed from first timestamp
        const firstTimestamp = new Date(graphData[0].Time).getTime();
      
        // 2. Filter and build an array of objects like { x, y }
        const filteredTempData = [];
        const filteredHumidityData = [];
      
        for (let i = 0; i < graphData.length; i++) {
          const timestamp = new Date(graphData[i].Time).getTime();
          const hoursElapsed = (timestamp - firstTimestamp) / (1000 * 60 * 60);
      
          // Check if hoursElapsed is in the user-defined min/max range
          if ((xMin === '' || hoursElapsed >= parseFloat(xMin)) &&
              (xMax === '' || hoursElapsed <= parseFloat(xMax))) {
            filteredTempData.push({
              x: hoursElapsed,
              y: parseFloat(graphData[i]['Temperature-1'])
            });
            filteredHumidityData.push({
              x: hoursElapsed,
              y: parseFloat(graphData[i]['Humidity-1'])
            });
          }
        }
      
        // 3. Return a Chart.js data object with a single dataset
        if (currentGraph === 'Temperature') {
          return {
            datasets: [
              {
                label: 'Temperature (°C)',
                data: filteredTempData,
                borderColor: 'rgba(75,192,192,1)',
                fill: false,
              }
            ]
          };
        } else {
          return {
            datasets: [
              {
                label: 'Humidity (%)',
                data: filteredHumidityData,
                borderColor: 'rgba(255,99,132,1)',
                fill: false,
              }
            ]
          };
        }
      };
      

    const toggleGraph = () => {
        setCurrentGraph(currentGraph === 'Temperature' ? 'Humidity' : 'Temperature');
    };

    const handleOpenModal = () => {
        setCurrentGraph('Temperature'); 
        fetchCSVData();
    };

    const handleCloseModal = () => {
        setOpenModal(false); 
    };

    const handleCloseParamsModal = () => {
        setOpenParamsModal(false);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    // Download menu handlers
    const handleDownloadClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleDownloadClose = () => {
        setAnchorEl(null);
    };

    const handleDownloadCSV = () => {
        if (!rawCSV) return;
        const blob = new Blob([rawCSV], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.href = url;
        link.setAttribute("download", `${currentGraph}.csv`);
        link.click();
        handleDownloadClose();
    };

    const handleDownloadImage = () => {
        if (!chartRef.current) return;
        const url = chartRef.current.toBase64Image();
        const link = document.createElement('a');
        link.download = `${currentGraph}.png`;
        link.href = url;
        link.click();
        handleDownloadClose();
    };

    const handleXAxisLabelChange = (event) => {
        setXAxisLabel(event.target.value);
    };

    const handleYAxisLabelChange = (event) => {
        setYAxisLabel(event.target.value);
    };

    const handleResetAxes = () => {
        setXAxisLabel(originalXAxisLabel);
        setYAxisLabel(originalYAxisLabel);
        setXMin('');
        setXMax('');
        setYMin('');
        setYMax('');
    };

    const handleXMinChange = (event) => {
        setXMin(event.target.value);
    };

    const handleXMaxChange = (event) => {
        setXMax(event.target.value);
    };

    const handleYMinChange = (event) => {
        setYMin(event.target.value);
    };

    const handleYMaxChange = (event) => {
        setYMax(event.target.value);
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2>{data[0] && data[0]['experiment_name'].split(":")[1]}</h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button variant="contained" onClick={handleOpenModal}>
                        Temperature/Humidity Graphs
                    </Button>
                    <Button variant="contained" color="secondary" onClick={fetchExperimentParams}>
                        Experiment Parameters
                    </Button>
                </div>
            </div>

            <div className="video-grid"> 
                {videos.map((video) => (
                    <div className="video-container" key={video.url}>
                        <h3>{video.title}</h3>
                        <video controls width="100%" autoPlay loop muted>
                            <source src={video.url} type={video.type || "video/mp4"} />
                            Your browser does not support the video tag.
                        </video>
                    </div>
                ))}
            </div>

            <Dialog open={openModal} onClose={handleCloseModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ backgroundColor: '#1e1e1e', color: '#ffffff' }}>
                    {`${currentGraph}`}
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1e1e1e', position: 'relative' }}>
                    <ArrowBackIos 
                        onClick={toggleGraph} 
                        style={{ position: 'absolute', left: '10px', top: '50%', color: 'white', cursor: 'pointer' }}
                    />
                    {graphData ? (
                        <Box>
                            <Line
                                ref={chartRef}
                                data={getGraphData()}
                                options={{
                                    scales: {
                                    x: {
                                        type: 'linear',
                                        ticks: { color: '#ffffff' },
                                        min: xMin ? parseFloat(xMin) : undefined,
                                        max: xMax ? parseFloat(xMax) : undefined,
                                        title: {
                                        display: true,
                                        text: xAxisLabel,
                                        color: '#ffffff'
                                        }
                                    },
                                    y: {
                                        type: 'linear',
                                        ticks: { color: '#ffffff' },
                                        min: yMin ? parseFloat(yMin) : undefined,
                                        max: yMax ? parseFloat(yMax) : undefined,
                                        title: {
                                        display: true,
                                        text: yAxisLabel,
                                        color: '#ffffff'
                                        }
                                    }
                                    },
                                    plugins: {
                                    legend: {
                                        labels: { color: '#ffffff' }
                                    }
                                    }
                                }}
                                />

                        </Box>
                    ) : (
                        <CircularProgress />
                    )}
                    <ArrowForwardIos 
                        onClick={toggleGraph} 
                        style={{ position: 'absolute', right: '50px', top: '50%', color: 'white', cursor: 'pointer' }}
                    />

                    {/* Download icon button */}
                    <IconButton
                        onClick={handleDownloadClick}
                        style={{ position: 'absolute', right: '10px', top: '10px', color: 'white' }}
                    >
                        <SaveAlt />
                    </IconButton>

                    <Menu
                        anchorEl={anchorEl}
                        open={openMenu}
                        onClose={handleDownloadClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <MenuItem onClick={handleDownloadCSV}>Download CSV Data</MenuItem>
                        <MenuItem onClick={handleDownloadImage}>Download Graph Image</MenuItem>
                    </Menu>

                    {/* Axes ranges input fields */}
                    <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                        <TextField
                            label="X-Axis Min (hours)"
                            variant="outlined"
                            value={xMin}
                            onChange={handleXMinChange}
                            fullWidth
                            sx={{ input: { color: 'white' }, label: { color: 'white' } }}
                        />
                        <TextField
                            label="X-Axis Max (hours)"
                            variant="outlined"
                            value={xMax}
                            onChange={handleXMaxChange}
                            fullWidth
                            sx={{ input: { color: 'white' }, label: { color: 'white' } }}
                        />
                        <TextField
                            label="Y-Axis Min"
                            variant="outlined"
                            value={yMin}
                            onChange={handleYMinChange}
                            fullWidth
                            sx={{ input: { color: 'white' }, label: { color: 'white' } }}
                        />
                        <TextField
                            label="Y-Axis Max"
                            variant="outlined"
                            value={yMax}
                            onChange={handleYMaxChange}
                            fullWidth
                            sx={{ input: { color: 'white' }, label: { color: 'white' } }}
                        />
                    </Box>
                </DialogContent>
            </Dialog>

            <Dialog open={openParamsModal} onClose={handleCloseParamsModal} maxWidth="md" fullWidth
                PaperProps={{
                    style: {
                        backgroundColor: '#1e1e1e',
                        color: '#ffffff',
                    },
                }}
            >
                <DialogTitle sx={{ backgroundColor: '#1e1e1e', color: '#ffffff' }}>Experiment Parameters</DialogTitle>
                <DialogContent sx={{ backgroundColor: '#1e1e1e' }}>
                    {experimentParams ? (
                        <Box sx={{ mt: 2 }}>
                            <Table>
                                <TableBody>
                                    {Object.entries(experimentParams).map(([key, value]) => {
                                        if(key !== 'iterations' && key !== 'task' && key !== 'experiment_running' && key !== 'exp_file' && key !== 'sample_names' && key !== 'advanced'){
                                            return (
                                                <TableRow key={key}>
                                                    <TableCell sx={{ color: '#ffffff', fontWeight: 'bold' }}>{key}</TableCell>
                                                    <TableCell sx={{ color: '#ffffff' }}>{String(value)}</TableCell>
                                                </TableRow>
                                            );
                                        }
                                        return null;
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    ) : (
                        <CircularProgress />
                    )}
                </DialogContent>
                <DialogActions sx={{ backgroundColor: '#1e1e1e' }}>
                    <Button onClick={handleCloseParamsModal} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={openSnackbar}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity="warning" sx={{ width: '100%' }}>
                    File not found
                </Alert>
            </Snackbar>
        </div>
    );
};

export default VideoDisplay;
